import { useState, useEffect, Suspense } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import {
  TableFiltersState,
  TableFilters,
} from "../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../common/services/company.api";

import { getVehicleTrackers } from "../../../common/services/vehicles.api";
import { IVehicle, VEHICLE_STATUS } from "../../../types/vehicle.type";
import { useCompanyStore } from "../../../common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [VEHICLE_STATUS.AVAILABLE]: "Disponível",
  [VEHICLE_STATUS.MISSING_DATA]: "Dados incompletos",
  [VEHICLE_STATUS.RENTED]: "Locado",
  [VEHICLE_STATUS.INACTIVE]: "Inativo",
  [VEHICLE_STATUS.WAITING_PAYMENT]: "Aguardando pagamento",
  [VEHICLE_STATUS.SELLED]: "Veículo vendido",
  [VEHICLE_STATUS.UNDER_PREPARATION]: "Em preparação",
  [VEHICLE_STATUS.UNDER_REVIEW]: "Revisão Programada",
  [VEHICLE_STATUS.IN_REPAIR]: "Em manutenção",
  [VEHICLE_STATUS.IN_REVISION]: "Reparo estético",
  [VEHICLE_STATUS.INSURANCE]: "Abertura de acionamento",
  [VEHICLE_STATUS.TRIGGER_OPENING]: "Proteção acionada",
  [VEHICLE_STATUS.IMPOUNDED]: "Apreendido",
  [VEHICLE_STATUS.THIRD_PARTY_INSURANCE]: "Seguro de Terceiros",
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: "Motos para Venda",
  [VEHICLE_STATUS.IN_TRANSIT]: "Em trânsito",
  [VEHICLE_STATUS.THEFT_ROBBERY]: "Furto/Roubo",
  [VEHICLE_STATUS.MISAPPROPRIATION]: "Apropriação indébita",
  [VEHICLE_STATUS.VEHICLE_ON_RESERVED]: "Moto reserva",
  [VEHICLE_STATUS.TRIAGE]: "Triagem",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsVehicle(status: VEHICLE_STATUS) {
  return STATUS_LABELS[status] || status;
}

const headers = {
  companyId: "Cidade",
  partnerInfoCompanyName: "Parceiro",
  licensePlate: "Placa",
  model: "Modelo",
  status: "Status",
  velotrackSettingsDate: "Data Últ. Comunicação",
};

function TableVehicleTrackers() {
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleTrackers, setVehicleTrackers] = useState<IVehicle[]>([]);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "velotrackSettings.lastComunication",
  });

  async function loadVehicleTrackers(filters, page) {
    try {
      setIsLoading(true);
      setVehicleTrackers([]);
      const result = await getVehicleTrackers({
        ...filters,
        page: page,
        itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
      });
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      setVehicleTrackers(result.data);
      setIsLoading(false);
    } catch (error) {
      notifyError("Ocorreu um erro inesperado");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadVehicleTrackers(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, notifyError, companyId, company]);

  const formatStyle = (
    content: React.ReactNode,
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    const lastComunicationStr = data.velotrackSettings?.lastComunication;

    if (!lastComunicationStr) {
      return <span className="text-redPrimary">{content}</span>;
    }

    const lastComunication = new Date(lastComunicationStr);
    const now = new Date();

    const diffInMs = now.getTime() - lastComunication.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 2) {
      return <span className="text-redPrimary">{content}</span>;
    }

    return <span className="text-black">{content}</span>;
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Rastreadores"
          placeholder="Digite a placa do veículo"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          entity="vehicle-trackers"
          tableData={vehicleTrackers as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={vehicleTrackers as unknown as Record<string, unknown>[]}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              partnerInfoCompanyName: (_, data) => {
                return formatStyle(
                  <span
                    className="flex justify-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {data.partnerInfo.companyName}
                  </span>,
                  data
                );
              },
              licensePlate: (_, data) => {
                return formatStyle(
                  <span
                    className="flex justify-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {data.licensePlate}
                  </span>,
                  data
                );
              },
              status: (_, data) => {
                return formatStyle(
                  <span
                    className="flex justify-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {getStatusLabelsVehicle(data.status)}
                  </span>,
                  data
                );
              },
              model: (_, data) => {
                return formatStyle(
                  <span
                    className="flex justify-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {data.model}
                  </span>,
                  data
                );
              },
              velotrackSettingsDate: (_, data) => {
                return formatStyle(
                  <span
                    className="flex justify-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {data.velotrackSettings.lastComunication &&
                      new Date(
                        data.velotrackSettings.lastComunication
                      ).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                  </span>,
                  data
                );
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="vehicleTrackers"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableVehicleTrackers;
